import { useAuth } from "@wtc/ui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchMessages } from "./api-client/index";
import type { MessageListResponse } from "@/lib/core/crm/types";
import type { MessageApiError } from "./error-types";

/**
 * Hook for fetching messages with React Query
 */
export function useMessagesList(
  provider: string,
  params: {
    connectionId: string;
    limit?: number;
    skip?: number;
    filter?: string;
  },
  enabled: boolean = true
) {
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  return useQuery<MessageListResponse, MessageApiError>({
    queryKey: ["messages", provider, params],
    queryFn: () => fetchMessages(provider, params, token || undefined),
    enabled: enabled && Boolean(provider && params.connectionId),
    staleTime: 5 * 60 * 1000, // 5 minutes to match server cache TTL (MESSAGE_CACHE_TTL.MESSAGE_LIST)
    refetchOnWindowFocus: false,
  });
}

/**
 * Mutation hook for loading more messages (pagination)
 */
type LoadMoreParams = {
  provider: string;
  params: {
    connectionId: string;
    limit?: number;
    skip?: number;
    filter?: string;
  };
};

export function useLoadMoreMessages() {
  const queryClient = useQueryClient();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  return useMutation<MessageListResponse, MessageApiError, LoadMoreParams>({
    mutationFn: ({ provider, params }) =>
      fetchMessages(provider, params, token || undefined),
    onSuccess: (data, variables) => {
      // Update the existing query data with new messages
      const queryKey = [
        "messages",
        variables.provider,
        {
          connectionId: variables.params.connectionId,
          limit: variables.params.limit,
          skip: 0, // Reset to original query
          filter: variables.params.filter,
        },
      ];

      queryClient.setQueryData<MessageListResponse | undefined>(queryKey, (oldData) => {
        if (!oldData) return data;

        return {
          ...data,
          messages: [...oldData.messages, ...data.messages],
        };
      });
    },
    onError: (error) => {
      toast.error(`Failed to load more messages: ${error.message}`);
    },
  });
}
